/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, FullscreenTakeover, toast } from "@jsq/ds/core";
import { CancelButton, Form } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { globalEnv } from "~/global-env";
import {
  AnnouncementFieldsFragmentDoc,
  useCreateAnnouncementMutation,
  useGetRolesQuery,
} from "~/graphql/supergraphHooks";
import { type ArenaGroup } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { toDbId } from "~/utils/graphql-helpers";
import { INITIAL_VALUES } from "./constants";
import DraftAnnouncementCard from "./DraftAnnouncementCard";
import PublishAnnouncementCard from "./PublishAnnouncementCard";
import { schema } from "./schema";
import TestAnnouncementCard from "./TestAnnouncementCard";
import { FormValues } from "./types";
import { getGrantedRoles, toCamelCase } from "./utils";

const ROLES_TO_EXCLUDE = new Set(["Investor Manager (Privileged) (View-only)"]);

const CreateAnnouncementForm = () => {
  const history = useHistory();
  const [testAnnouncementCreated, setTestAnnouncementCreated] = useState<boolean>(false);
  const [arenaSelectionError, setArenaSelectionError] = useState<string>("");

  /**
   * Handles what happens when the user clicks the submit button.
   *
   * @param values The values the user filled out.
   */
  const [createAnnouncementMutation] = useCreateAnnouncementMutation({
    awaitRefetchQueries: true,
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
    update(cache, { data }) {
      if (!data || !data.createAnnouncement) {
        return;
      }

      cache.modify({
        fields: {
          announcements(existingAnnouncements = []) {
            const newAnnouncementRef = cache.writeFragment({
              data: data?.createAnnouncement,
              fragment: AnnouncementFieldsFragmentDoc,
            });
            return [...existingAnnouncements, newAnnouncementRef];
          },
        },
      });
    },
  });

  /**
   * Handles what to do if the user exits without submitting a case.
   */
  const onCancelExit = () => {
    history.push(`${globalEnv.enablementRoute}`);
  };

  const rolesResult = useGetRolesQuery();
  const rolesData = rolesResult?.data?.roles || [];
  const allRoleIds = rolesData.map((role) => role.id);
  const allRoleIdsAsDbIds = allRoleIds.map((id) => toDbId(id));
  const isManageAdminConfigurationSetting = rolesData.filter(
    (role) => role.isManageAdminConfigurationSetting,
  );
  const isObjectLevelBasicPermissionSettings = rolesData.filter(
    (role) => role.isObjectLevelBasicPermission,
  );
  const isManageStaffAccessSetting = rolesData.filter(
    (role) => role.isManageStaffAccessSetting,
  );
  const isManageBasicConfigurationSetting = rolesData?.filter(
    (role) => role.isManageBasicConfigurationSetting,
  );

  const filteredRoles = rolesData?.filter(
    (role) =>
      !role.isManageBasicConfigurationSetting &&
      !role.isManageStaffAccessSetting &&
      !role.isManageAdminConfigurationSetting &&
      !role.isObjectLevelBasicPermission &&
      !role.isNonStaffRole &&
      !ROLES_TO_EXCLUDE.has(role.name ?? "") &&
      // only include roles that are handled by the form schema
      toCamelCase(role.name ?? "") in INITIAL_VALUES,
  );

  const camelCasedRoles = filteredRoles?.map((role) => ({
    ...role,
    name: toCamelCase(role.name ?? ""),
  }));

  const handleSubmit = async (values: FormValues): Promise<void> => {
    let convertToDBIds: number[] = [];
    if (values.audience === "select") {
      convertToDBIds = getGrantedRoles(
        camelCasedRoles,
        isManageStaffAccessSetting,
        isManageBasicConfigurationSetting,
        isManageAdminConfigurationSetting,
        isObjectLevelBasicPermissionSettings,
        values,
      );

      if (convertToDBIds.length === 0) {
        toast.error("Please select at least one role");
        return;
      }
    } else {
      convertToDBIds = allRoleIdsAsDbIds;
    }
    let arenaGroups: ArenaGroup[] = [];
    if (values.isTestAnnouncement) {
      arenaGroups = ["TEST_ARENA_ONLY"];
    } else if (values.arenaType === "allArenas") {
      arenaGroups = ["FA_ONLY", "NON_FA_ONLY"];
    } else if (values.arenaType === "faArenas") {
      arenaGroups = ["FA_ONLY"];
    } else if (values.arenaType === "nonFAarenas") {
      arenaGroups = ["NON_FA_ONLY"];
    } else {
      setArenaSelectionError("Please select an arena type");
      return;
    }

    try {
      await createAnnouncementMutation({
        variables: {
          input: {
            name: values.name,
            header: values.subject,
            body: values.body,
            url: values.hyperlink,
            arenaGroups: arenaGroups,
            userRoles: convertToDBIds,
            expiredAt: values.expiryDate && values.expiryDate.toISOString().split("T")[0],
          },
        },
      });
      if (values.isTestAnnouncement) {
        toast.success("Test announcement sent!");
        setTestAnnouncementCreated(true);
      } else {
        toast.success("Announcement is published!");
        onCancelExit();
      }
    } catch {
      toast.error("Failed to create announcement, please try again.");
    }
  };

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        onExit={onCancelExit}
        title={"Create new in-app announcement"}
      />
      <Form
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        validationSchema={schema}
        submitOnlyChangedValues={false}
      >
        <FullscreenTakeover.Body>
          <Column size="sm">
            <DraftAnnouncementCard camelCasedRoles={camelCasedRoles} />
            <TestAnnouncementCard />
            {testAnnouncementCreated && (
              <PublishAnnouncementCard
                arenaSelectionError={arenaSelectionError}
                setArenaSelectionError={setArenaSelectionError}
              />
            )}

            <Flex justifyContent="flex-end">
              <CancelButton onClick={onCancelExit} css={{ marginRight: SPACING.MEDIUM }} />
            </Flex>
          </Column>
        </FullscreenTakeover.Body>
      </Form>
    </FullscreenTakeover>
  );
};

export default CreateAnnouncementForm;
